import React, { useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import moment from "moment";
import PropTypes from "prop-types";
import { Button, Grid, TextField, useMediaQuery } from "@mui/material";

import {
  areaCategoryEnum,
  fireSafetyReportOptions,
  getOptionLabel,
  getOptionValue,
  halfYearEnum,
} from "common/enums";
import { dateFormats } from "common/constant";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import DatePicker from "Components/Common/DatePicker";
import BaseTable from "Components/Common/BaseTable";
import ConsumerAndMeteringStatusTable from "pages/Admin/Reports/ConsumerAndMeteringStatusTable";

import { filterEmptyStrings } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";

import { useLazyDownloadExcelQuery, useLazyGetQuery } from "slices/api";
import { useLazyGetDeveloperBuildingComplianceQuery } from "apis/FireSafety/DashBoard";

const BY_DEFAULT_VALUE = 1;
const RECORDS_PER_PAGE = 20;

const applyColumnPropsOnKey = "connectionAddress";

const getTableColumns = (
  columns,
  columnData = {},
  fieldKey,
  valueKey,
  parentKey = ""
) => {
  Object.keys(columnData).forEach((key) => {
    if (columnData[key] && typeof columnData[key] === "object") {
      getTableColumns(
        columns,
        columnData[key],
        fieldKey,
        valueKey,
        `${parentKey ? `${parentKey}.` : ""}${key}`
      );
    } else {
      columns.push({
        [fieldKey]: convertCamelTextToCapitalizeText(key),
        [valueKey]: `${parentKey ? `${parentKey}.` : ""}${key}`,
        columnProps:
          applyColumnPropsOnKey === key ? { style: { minWidth: "350px" } } : {},
      });
    }
  });
};

const FireSafetyReports = ({ t }) => {
  const [selectedReportId, setSelectedReportId] = useState(BY_DEFAULT_VALUE);
  const [rowTableData, setRowTableData] = useState([]);
  const [query, setQuery] = useState({
    year: moment(),
    halfYear: "",
    developerBuildingId: "",
    areaCategory: "",
  });

  const sm = useMediaQuery("(max-width: 599px)");

  const [
    getDeveloperBuildingCompliance,
    {
      data: { data: developerBuildingCompliance } = {},
      isLoading: isDeveloperBuildingComplianceLoading,
    },
  ] = useLazyGetDeveloperBuildingComplianceQuery();

  const [
    downloadExcel,
    { isLoading: isExcelFileDownloading, isFetching: isDownloadExcelFetching },
  ] = useLazyDownloadExcelQuery();

  const [
    getTableData,
    { data: { data: tableData } = {}, isLoading, isFetching },
  ] = useLazyGetQuery();

  const reportsData = useMemo(
    () => [
      {
        value: 1,
        endpoint:
          "/inspection-observation/report/all-developer-building-compliance",
        downloadSheetEndpoint:
          "/inspection-observation/report/all-developer-building-compliance-excel",
        sheetName: "Building Compliance",
      },
      {
        value: 2,
        endpoint:
          "/inspection-observation/report/common-inspection-observation",
        downloadSheetEndpoint:
          "/inspection-observation/report/common-inspection-observation-excel",
        sheetName: "Common Inspection Observation",
      },
      {
        value: 3,
        endpoint: "/schedule/report/overallcompliance-city-level",
        downloadSheetEndpoint:
          "/schedule/report/overallcompliance-city-level-excel",
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "overallComplianceMonthWise",
            "overallComplianceCityCount",
            "category",
            "count",
          ],
        },
        sheetName: "Overall Compliance (City Level)",
      },
      {
        value: 4,
        endpoint: "/schedule/report/inspection-status",
        downloadSheetEndpoint: "/schedule/report/inspection-status-excel",
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "overallComplianceMonthWise",
            "overallComplianceCityCount",
            "category",
            "count",
          ],
        },
        sheetName: "Inspection Status",
      },
      {
        value: 5,
        endpoint: "/schedule/report/all-building-compliance-rate-over-time",
        downloadSheetEndpoint:
          "/schedule/report/compliance-rate-over-time-excel",
        sheetName: "Compliance Rate Over Time",
      },
      {
        value: 6,
        endpoint:
          "/inspection-observation/report/developer-building-observation",
        downloadSheetEndpoint:
          "/inspection-observation/report/developer-building-observation-excel",
        sheetName: "Building Observation",
      },
      {
        value: 7,
        endpoint:
          "/inspection-observation/report/non-compliance-corrective-action-status-building",
        downloadSheetEndpoint:
          "/inspection-observation/report/non-compliance-corrective-action-status-building-excel",
        sheetName: "Building Non-Compliance Status",
      },
    ],
    [query.reportType]
  );

  const {
    endpoint,
    Component,
    downloadSheetEndpoint,
    isServerSidePaginationRequired,
    sheetName,
    componentProps,
  } = useMemo(
    () => reportsData.find(({ value }) => value === selectedReportId),
    [selectedReportId, reportsData]
  );

  const buildingOptions = useMemo(
    () =>
      developerBuildingCompliance?.map((building) => ({
        label: building.name,
        value: building.id,
      })) ?? [],
    [developerBuildingCompliance]
  );

  const isBuildingComplianceSelected = useMemo(
    () =>
      getOptionLabel("fireSafetyReportOptions", selectedReportId) ===
      "Building Compliance",
    [selectedReportId]
  );

  const isComplianceRateOverTimeSelected = useMemo(
    () =>
      getOptionLabel("fireSafetyReportOptions", selectedReportId) ===
      "Compliance Rate Over Time",
    [selectedReportId]
  );

  const isBuildingObservationSelected = useMemo(
    () =>
      getOptionLabel("fireSafetyReportOptions", selectedReportId) ===
      "Building Observation",
    [selectedReportId]
  );

  const columns = useMemo(() => {
    let tableColumns = [];
    getTableColumns(tableColumns, tableData?.[0], "title", "field");
    return tableColumns;
  }, [tableData, selectedReportId]);

  const getReportData = async () => {
    try {
      const { data: { data: tableData } = {} } = await getTableData(
        {
          endpoint,
          query: {
            year: query.year.format("YYYY"),
            ...(!isComplianceRateOverTimeSelected && {
              halfYear: query.halfYear,
            }),
            ...((isBuildingComplianceSelected ||
              isBuildingObservationSelected) && {
              developerBuildingId: query.developerBuildingId,
            }),
            ...(isBuildingObservationSelected && {
              areaCategory: query.areaCategory,
            }),
          },
        },
        true
      );
      if (tableData) {
        const data = Array.isArray(tableData) ? tableData : [tableData];

        const updatedTableData = data.map((row) =>
          Object.entries(row).reduce((res, [key, value]) => {
            if (
              typeof value === "string" &&
              moment(value, dateFormats, true).isValid()
            ) {
              return {
                ...res,
                [key]: convertUTCDateToLocalDate(value).format("DD-MM-YYYY"),
              };
            }
            return { ...res, [key]: value };
          }, {})
        );
        setRowTableData(updatedTableData || []);
      }
    } catch (error) {}
  };

  const getReportUrl = async () => {
    if (downloadSheetEndpoint) {
      try {
        await downloadExcel({
          endpoint: downloadSheetEndpoint,
          sheetName,
          query: filterEmptyStrings({
            ...query,
            year: query.year.format("YYYY"),
          }),
        });
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (!isServerSidePaginationRequired) {
      getReportData();
    }
  }, [endpoint, query]);

  // calling building options api
  useEffect(() => {
    (async () => {
      if (
        selectedReportId ===
          getOptionValue("fireSafetyReportOptions", "Building Compliance") ||
        selectedReportId ===
          getOptionValue("fireSafetyReportOptions", "Building Observation")
      ) {
        await getDeveloperBuildingCompliance({
          year: query.year.format("YYYY"),
          ...(query.halfYear && { halfYear: query.halfYear }),
        });
      }
    })();
  }, [query]);

  useEffect(() => {
    if (isBuildingObservationSelected && buildingOptions.length) {
      setQuery({ ...query, developerBuildingId: buildingOptions[0]?.value });
    }
  }, [isBuildingObservationSelected, buildingOptions]);

  return (
    <div className="h-100">
      <Backdrop
        open={
          isLoading ||
          isFetching ||
          isExcelFileDownloading ||
          isDownloadExcelFetching ||
          isDeveloperBuildingComplianceLoading
        }
      />

      <BreadCrumb title={t("Reports")} pageTitle={t("Dashboard")} />

      <Grid container columnSpacing={2} rowSpacing={1} mt="7px" mb="7px">
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            onChange={(_, { value }) => {
              setSelectedReportId(value);
              setRowTableData([]);
            }}
            value={fireSafetyReportOptions.find(
              ({ value }) => value === selectedReportId
            )}
            options={fireSafetyReportOptions}
            renderInput={(params) => {
              delete params.inputProps.id;
              delete params.id;
              return <TextField {...params} label={t("Select Report Type")} />;
            }}
          />
        </Grid>

        <Grid item xs={8} sm={5} md={4} lg={2}>
          <DatePicker
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            label={t("Select Year")}
            className="w-100"
            views={["year"]}
            value={query.year}
            disableFuture
            onChange={(date) => {
              setQuery({
                ...query,
                year: date,
              });
            }}
          />
        </Grid>

        {!isComplianceRateOverTimeSelected && (
          <Grid item xs={8} sm={5} md={4} lg={2}>
            <SelectField
              label={t("Select half")}
              disableClearable={false}
              options={halfYearEnum}
              value={
                halfYearEnum.find(({ value }) => value === query.halfYear) ?? ""
              }
              onChange={(_, option) =>
                setQuery({
                  ...query,
                  halfYear: option?.value ?? "",
                })
              }
            />
          </Grid>
        )}

        {!isComplianceRateOverTimeSelected &&
          (isBuildingComplianceSelected || isBuildingObservationSelected) && (
            <Grid item xs={8} sm={5} md={4} lg={3}>
              <SelectField
                label={t("Select building")}
                disableClearable={false}
                options={buildingOptions}
                value={
                  buildingOptions.find(
                    ({ value }) => value === query.developerBuildingId
                  ) ?? ""
                }
                onChange={(_, option) =>
                  setQuery({
                    ...query,
                    developerBuildingId: option?.value ?? "",
                  })
                }
              />
            </Grid>
          )}
        {!isComplianceRateOverTimeSelected && isBuildingObservationSelected && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Select Area")}
              disableClearable={false}
              options={areaCategoryEnum}
              value={
                areaCategoryEnum.find(
                  ({ value }) => value === query.areaCategory
                ) ?? ""
              }
              onChange={(_, option) =>
                setQuery({
                  ...query,
                  areaCategory: option?.value ?? "",
                })
              }
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={4} lg={2} sx={{ pt: 1.25, pl: 2 }}>
          <Button
            sx={{ maxHeight: "40px" }}
            variant="contained"
            color="primary"
            onClick={getReportUrl}
          >
            {t("Download")}
          </Button>
        </Grid>
      </Grid>

      {(!!rowTableData.length || isServerSidePaginationRequired) &&
      Component ? (
        <Component
          data={rowTableData}
          {...(componentProps || {})}
          maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
        />
      ) : (
        <BaseTable
          columns={columns}
          data={rowTableData}
          pageSize={RECORDS_PER_PAGE}
          maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
          displaySrNo={!isBuildingComplianceSelected}
        />
      )}
    </div>
  );
};

FireSafetyReports.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyReports);
